import { useState, useEffect } from 'react';
import { api } from '../api';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const STATUS_COLOR = { done: 'var(--accent)', error: 'var(--red)', transcribing: 'var(--blue)', analysing: 'var(--yellow)', pending: 'var(--text-3)' };

function fmt(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function MeetingsPage({ onSelectMeeting, onNewMeeting, refreshTrigger }) {
  const [meetings, setMeetings] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async (q = '') => {
    try {
      const res = await fetch(`${BASE}/api/meetings${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      const data = await res.json();
      setMeetings(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshTrigger]);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => {
    const hasActive = meetings.some(m => m.status !== 'done' && m.status !== 'error');
    if (!hasActive) return;
    const t = setInterval(() => load(search), 3000);
    return () => clearInterval(t);
  }, [meetings, search]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this meeting?')) return;
    await api.deleteMeeting(id);
    setMeetings(prev => prev.filter(m => m.id !== id));
  };

  const filtered = meetings.filter(m => filter === 'all' || m.status === filter);
  const counts = { all: meetings.length, done: meetings.filter(m => m.status === 'done').length, error: meetings.filter(m => m.status === 'error').length };

  return (
    <div className="page fade-in">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Meetings</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{counts.done} analysed · {meetings.length} total</div>
        </div>
        <div style={{ display: 'flex', gap: '0.625rem' }}>
          <button className="btn btn-outline btn-sm" onClick={() => onNewMeeting('transcript')}>✎ Paste Transcript</button>
          <button className="btn btn-primary btn-sm" onClick={() => onNewMeeting('file')}>+ Upload Meeting</button>
        </div>
      </div>

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', fontSize: 14 }}>⌕</span>
          <input type="text" placeholder="Search meetings..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32 }} />
        </div>
        <div style={{ display: 'flex', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 3, gap: 2 }}>
          {['all', 'done', 'error'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
              background: filter === f ? 'var(--bg-4)' : 'transparent',
              color: filter === f ? 'var(--text)' : 'var(--text-3)',
              transition: 'all 0.15s',
            }}>
              {f} {counts[f] > 0 && <span style={{ opacity: 0.6 }}>({counts[f]})</span>}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {[1,2,3,4,5].map(i => <div key={i} className="skeleton" style={{ height: 76, borderRadius: 10 }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, padding: '4rem 2rem', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: '1rem' }}>📭</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>{search ? 'No results found' : 'No meetings yet'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: '1.5rem' }}>{search ? 'Try a different search term' : 'Start by uploading a recording or pasting a transcript'}</div>
          {!search && (
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button className="btn btn-primary btn-sm" onClick={() => onNewMeeting('file')}>↑ Upload Recording</button>
              <button className="btn btn-outline btn-sm" onClick={() => onNewMeeting('transcript')}>✎ Paste Transcript</button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {filtered.map(m => (
            <div key={m.id} onClick={() => onSelectMeeting(m.id)} style={{
              display: 'flex', alignItems: 'center', gap: '1rem',
              padding: '1rem 1.25rem',
              background: 'var(--bg-2)', border: '1px solid var(--border)',
              borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-2)'; e.currentTarget.style.background = 'var(--bg-3)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-2)'; }}
            >
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLOR[m.status], flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  {fmt(m.created_at)}
                  {m.summary ? ` · ${m.summary.slice(0, 90)}...` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'center', flexShrink: 0 }}>
                <span className={`badge badge-${m.status}`}>{m.status}</span>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ color: 'var(--text-3)', padding: '2px 6px', fontSize: 13 }}
                  onClick={e => handleDelete(e, m.id)}
                >✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
